import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Grid, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, ListItemText, Autocomplete, Chip, Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import { createDiscount, updateDiscount } from '../../../services/DiscountService';
import { getProducts } from '../../../services/ProductService';
import { getCategoriesByType } from '../../../services/CategoryService';
import { useTranslation } from 'react-i18next';
import { discountType, getDiscountTypeLabel, categoryOptions } from '../../../utils/Enums';
dayjs.extend(require('dayjs/plugin/utc'));
import dayjs from 'dayjs';

const dayOptions = [
  { code: 'L', label: 'Lunes' },
  { code: 'M', label: 'Martes' },
  { code: 'X', label: 'Miércoles' },
  { code: 'J', label: 'Jueves' },
  { code: 'V', label: 'Viernes' },
  { code: 'S', label: 'Sábado' },
  { code: 'D', label: 'Domingo' },
];

const DiscountModal = ({ locationId, open, onClose, onSuccess, discountToEdit }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    code: '',
    type: discountType.PERCENTAGE,
    percentage: '',
    buyQuantity: '',
    payQuantity: '',
    maxDiscountAmount: '',
    maxUses: '',
    startDate: '',
    endDate: '',
    recurrence: [],
    description: '',
    isAutoApplicable: false,
    validForWeb: false,
  });
  const [loading, setLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryAutocompleteValue, setCategoryAutocompleteValue] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (open) {
      const loadProductsAndSetForm = async () => {
        try {
          const categories = await getCategoriesByType(categoryOptions.PRODUCTS);
          const categoryList = Array.isArray(categories) ? categories : [];
          setAvailableCategories(categoryList);
          const products = await getProducts(locationId, 1, 100000, '');
          if (products && typeof products === 'object') {
            setAvailableProducts(products.products || products.data || []);
          } else {
            setAvailableProducts(products || []);
          }

          if (discountToEdit) {
            const { discount, percentageDetails, buyXPayYDetails } = discountToEdit;
            const details = percentageDetails || buyXPayYDetails;
            const isBuyXPayY = discount.type === discountType.BUY_X_PAY_Y;

            setFormData({
              code: discount.code || '',
              type: discount.type || discountType.PERCENTAGE,
              percentage: percentageDetails?.percentage ?? '',
              buyQuantity: buyXPayYDetails?.buyQuantity ?? '',
              payQuantity: buyXPayYDetails?.payQuantity ?? '',
              maxDiscountAmount: discount.maxDiscountAmount || '',
              maxUses: discount.maxUses || '',
              startDate: discount.startDate || '',
              endDate: discount.endDate || '',
              recurrence: discount.recurrence ? discount.recurrence.split(';') : [],
              description: discount.description,
              isAutoApplicable: discount.isAutoApplicable ?? false,
              validForWeb: discount.validForWeb ?? false,
            });

            if (Array.isArray(details?.products)) {
              const productList = products?.products ?? products?.data ?? (Array.isArray(products) ? products : []);
              const selected = (productList || []).filter(p => details.products.includes(p.id));
              setSelectedProducts(selected);
            }
            if (Array.isArray(details?.categories)) {
              setSelectedCategories(categoryList.filter(c => details.categories.includes(c.id)));
            }
          }
        } catch (err) {
          console.error('Error loading products or discount', err);
        }
      };

      loadProductsAndSetForm();
    } else {
      setFormData({
        code: '',
        type: discountType.PERCENTAGE,
        percentage: '',
        buyQuantity: '',
        payQuantity: '',
        maxDiscountAmount: '',
        maxUses: '',
        startDate: '',
        endDate: '',
        recurrence: [],
        description: '',
        isAutoApplicable: false,
        validForWeb: false,
      });
      setSelectedProducts([]);
      setSelectedCategories([]);
      setLoading(false);
    }
  }, [open, discountToEdit]);

  const handleAddProduct = (event, newValue) => {
    if (newValue && !selectedProducts.some(p => p.id === newValue.id)) {
      setSelectedProducts(prev => [...prev, newValue]);
    }
    setAutocompleteValue(null);
  };

  const handleRemoveProduct = (id) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
  };

  const handleAddCategory = (event, newValue) => {
    if (newValue && !selectedCategories.some(c => c.id === newValue.id)) {
      setSelectedCategories(prev => [...prev, newValue]);
    }
    setCategoryAutocompleteValue(null);
  };

  const handleRemoveCategory = (id) => {
    setSelectedCategories(prev => prev.filter(c => c.id !== id));
  };

  const isValidCode = (code) => /^[a-zA-Z0-9_-]+$/.test(code);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!isValidCode(formData.code)) {
        setErrorMessage('El código solo puede contener letras, números, guiones medios y guiones bajos.');
        setShowError(true);
        setLoading(false);
        return;
      }

      const isBuyXPayY = formData.type === discountType.BUY_X_PAY_Y;
      if (isBuyXPayY) {
        const buyQ = parseInt(formData.buyQuantity, 10);
        const payQ = parseInt(formData.payQuantity, 10);
        if (isNaN(buyQ) || isNaN(payQ) || buyQ < 1 || payQ < 1) {
          setErrorMessage('Lleva y Paga deben ser números mayores a 0.');
          setShowError(true);
          setLoading(false);
          return;
        }
        if (payQ > buyQ) {
          setErrorMessage('Paga (Y) no puede ser mayor que Lleva (X).');
          setShowError(true);
          setLoading(false);
          return;
        }
      }

      const payload = {
        type: formData.type,
        location_id: parseInt(locationId),
        code: formData.code,
        max_discount_amount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        max_uses: formData.maxUses ? parseInt(formData.maxUses) : null,
        recurrence: formData.recurrence.length > 0 ? formData.recurrence.join(';') : null,
        description: formData.description,
        start_date: formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        end_date: formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        products: selectedProducts.length > 0 ? selectedProducts.map(p => p.id) : null,
        categories: selectedCategories.length > 0 ? selectedCategories.map(c => c.id) : null,
        is_auto_applicable: formData.isAutoApplicable,
        valid_for_web: formData.validForWeb,
      };
      if (isBuyXPayY) {
        payload.buy_quantity = parseInt(formData.buyQuantity, 10);
        payload.pay_quantity = parseInt(formData.payQuantity, 10);
      } else {
        payload.percentage = parseFloat(formData.percentage);
      }

      let success = false;
      if (discountToEdit) {
        success = await updateDiscount(discountToEdit.discount.id, payload);
      } else {
        success = await createDiscount(payload);
      }

      if (success) {
        setLoading(false);
        onSuccess();
        onClose();
      } else {
        setErrorMessage('Ocurrió un error inesperado al guardar.');
        setShowError(true);
      }
    } catch (e) {
      setLoading(false);

      const responseMessage = e.response?.data?.error_message || e.response?.data?.message;
      if (responseMessage) {
        setErrorMessage(responseMessage);
        setShowError(true);
      } else {
        setErrorMessage('Ocurrió un error al guardar el descuento.');
        setShowError(true);
      }

      console.error('Error submitting discount:', e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('new_discount')}</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Tooltip title="Código que identifica el descuento." placement="top-start">
                <TextField
                  label="Código del descuento"
                  fullWidth
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de descuento</InputLabel>
                <Select
                  label="Tipo de descuento"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  <MenuItem value={discountType.PERCENTAGE}>{getDiscountTypeLabel(discountType.PERCENTAGE)}</MenuItem>
                  <MenuItem value={discountType.BUY_X_PAY_Y}>{getDiscountTypeLabel(discountType.BUY_X_PAY_Y)}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.type === discountType.PERCENTAGE && (
              <Grid item xs={6}>
                <Tooltip title="Porcentaje que se aplicará sobre el precio del producto." placement="top-start">
                  <TextField
                    label="Porcentaje de descuento"
                    type="number"
                    fullWidth
                    value={formData.percentage}
                    onChange={(e) => handleChange('percentage', e.target.value)}
                  />
                </Tooltip>
              </Grid>
            )}
            {formData.type === discountType.BUY_X_PAY_Y && (
              <>
                <Grid item xs={6}>
                  <Tooltip title="Cantidad de unidades que se llevan (X)." placement="top-start">
                    <TextField
                      label="Lleva (X)"
                      type="number"
                      inputProps={{ min: 1 }}
                      fullWidth
                      value={formData.buyQuantity}
                      onChange={(e) => handleChange('buyQuantity', e.target.value)}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Cantidad de unidades que se pagan (Y). Debe ser menor o igual a X." placement="top-start">
                    <TextField
                      label="Paga (Y)"
                      type="number"
                      inputProps={{ min: 1 }}
                      fullWidth
                      value={formData.payQuantity}
                      onChange={(e) => handleChange('payQuantity', e.target.value)}
                    />
                  </Tooltip>
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <Tooltip title="Monto máximo de descuento permitido por aplicación." placement="top-start">
                <TextField
                  label="Tope máximo"
                  type="number"
                  fullWidth
                  value={formData.maxDiscountAmount}
                  onChange={(e) => handleChange('maxDiscountAmount', e.target.value)}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip title="Cantidad máxima de veces que puede utilizarse este descuento." placement="top-start">
                <TextField
                  label="Máx. usos"
                  type="number"
                  fullWidth
                  value={formData.maxUses}
                  onChange={(e) => handleChange('maxUses', e.target.value)}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="Descripción del descuento." placement="top-start">
                <TextField
                  label="Descripción"
                  type={'text'}
                  fullWidth
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={6}>
              <Tooltip title="Fecha en que comenzará a aplicarse el descuento." placement="top-start">
                <DateTimePicker
                  label="Fecha inicio"
                  value={formData.startDate ? dayjs(formData.startDate) : null}
                  onChange={(date) => handleChange('startDate', date ? date.format('YYYY-MM-DDTHH:mm:ss') : '')}
                  renderInput={(params) => <TextField {...params} fullWidth />} />
              </Tooltip>
            </Grid>

            <Grid item xs={6}>
              <Tooltip title="Fecha en que dejará de aplicarse el descuento." placement="top-start">
                <DateTimePicker
                  label="Fecha fin"
                  value={formData.endDate ? dayjs(formData.endDate) : null}
                  onChange={(date) => handleChange('endDate', date ? date.format('YYYY-MM-DDTHH:mm:ss') : '')}
                  renderInput={(params) => <TextField {...params} fullWidth />} />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="Días de la semana en que el descuento estará activo." placement="top-start">
                <FormControl fullWidth>
                  <InputLabel>{t('recurrence')}</InputLabel>
                  <Select
                    label={t('recurrence')}
                    multiple
                    value={formData.recurrence}
                    onChange={(e) => handleChange('recurrence', e.target.value)}
                    renderValue={(selected) => selected.map(val => {
                      const found = dayOptions.find(day => day.code === val);
                      return found ? found.label : val;
                    }).join('; ')}
                  >
                    {dayOptions.map((day) => (
                      <MenuItem key={day.code} value={day.code}>
                        <Checkbox checked={formData.recurrence.includes(day.code)} />
                        <ListItemText primary={day.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.validForWeb}
                    onChange={(e) => handleChange('validForWeb', e.target.checked)}
                    color="primary"
                  />
                }
                label="Válido en la tienda web (ecommerce)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isAutoApplicable}
                    onChange={(e) => handleChange('isAutoApplicable', e.target.checked)}
                  />
                }
                label="Autoaplicable"
              />
            </Grid>

            <Grid item xs={12}><Box sx={{ borderTop: '1px solid #ccc', my: 2 }} /></Grid>

            <Grid item xs={12}>
              <Tooltip title="Seleccioná los productos a los que se aplicará este descuento. Si no elegís productos ni categorías, se aplicará a todos." placement="top-start">
                <Autocomplete
                  options={availableProducts}
                  getOptionLabel={(option) => option.name}
                  value={autocompleteValue}
                  onChange={handleAddProduct}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Agregar producto"
                      fullWidth
                    />
                  )}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedProducts.map((product) => (
                  <Chip
                    key={product.id}
                    label={product.name}
                    onDelete={() => handleRemoveProduct(product.id)}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="El descuento se aplicará a todos los productos de las categorías elegidas (además de los productos individuales de arriba)." placement="top-start">
                <Autocomplete
                  options={availableCategories.filter(c => !selectedCategories.some(s => s.id === c.id))}
                  getOptionLabel={(option) => option.name}
                  value={categoryAutocompleteValue}
                  onChange={handleAddCategory}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Agregar categoría"
                      fullWidth
                    />
                  )}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedCategories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleRemoveCategory(category.id)}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton onClick={handleSubmit} loading={loading} variant="contained">
          {t('save')}
        </LoadingButton>
      </DialogActions>

      <Snackbar open={showError} autoHideDuration={4000} onClose={() => setShowError(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DiscountModal;
